import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import ThemeToggle from "../../../components/ThemeToggle";
import logo from "../../../assets/logo.svg";
import ENDPOINTS from "../../../api/endpoint";
import { showSuccessToast, showErrorToast } from "../../../components/Toast";
import Loader from "../../../components/Loader";

export default function ForgotPassword({ theme, setTheme }) {
  const navigate = useNavigate();
  // step 1 = enter email, step 2 = enter code + new password
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Step 1: request a reset code
  const requestCode = async (e) => {
    e.preventDefault();
    if (!email) {
      showErrorToast("Enter your email");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.FORGOT_PASSWORD, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        showErrorToast(data.detail || "Could not send reset code ❌");
        throw new Error(data.detail || "forgot password failed");
      }
      showSuccessToast("If that email exists, a reset code is on its way 📩");
      setStep(2);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: verify code and set the new password
  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      showErrorToast("Enter the 6-digit code from your email");
      return;
    }
    if (newPassword.length < 6) {
      showErrorToast("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      showErrorToast("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.RESET_PASSWORD, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          otp_code: otpCode,
          new_password: newPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showErrorToast(data.detail || "Could not reset password ❌");
        throw new Error(data.detail || "reset password failed");
      }
      showSuccessToast("Password reset. Please log in ✅");
      navigate("/");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (!email) return;
    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.FORGOT_PASSWORD, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        showErrorToast(data.detail || "Could not resend code ❌");
        throw new Error(data.detail || "resend failed");
      }
      showSuccessToast("Reset code resent 📩");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen relative">
      {loading && <Loader />}

      {/* Left side - form */}
      <div className="w-full md:w-1/2 flex flex-col justify-center px-8 lg:px-20 bg-white dark:bg-gray-900 transition-colors duration-300 relative">
        <div className="absolute top-4 right-4 md:hidden">
          <ThemeToggle theme={theme} setTheme={setTheme} />
        </div>

        <div className="max-w-md w-full mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Reset password
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {step === 1
              ? "Enter your email and we'll send you a reset code"
              : "Enter the code we emailed you and choose a new password"}
          </p>

          {step === 1 ? (
            <form className="space-y-5" onSubmit={requestCode}>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email<span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 bg-indigo-900 text-white font-medium rounded-md shadow hover:bg-indigo-800 transition disabled:opacity-50"
              >
                {loading ? "Sending..." : "Send reset code"}
              </button>
            </form>
          ) : (
            <form className="space-y-5" onSubmit={submitNewPassword}>
              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reset code<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) =>
                    setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="6-digit code"
                  className="w-full px-4 py-2 border rounded-md tracking-widest focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  required
                />
              </div>

              {/* New password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  New password<span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    value={newPassword}
                    onChange={(e) =>
                      setNewPassword(e.target.value.replace(/\s/g, ""))
                    }
                    placeholder="Min. 6 characters"
                    className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500 dark:text-gray-300"
                  >
                    {showPassword ? (
                      <AiOutlineEyeInvisible size={20} />
                    ) : (
                      <AiOutlineEye size={20} />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm new password<span className="text-red-500">*</span>
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) =>
                    setConfirmPassword(e.target.value.replace(/\s/g, ""))
                  }
                  placeholder="Re-enter new password"
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 bg-indigo-900 text-white font-medium rounded-md shadow hover:bg-indigo-800 transition disabled:opacity-50"
              >
                {loading ? "Resetting..." : "Reset password"}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={resendCode}
                  disabled={loading}
                  className="text-indigo-900 dark:text-white hover:underline disabled:opacity-50"
                >
                  Resend code
                </button>
              </div>
            </form>
          )}

          <p className="mt-6 text-sm text-center text-gray-600 dark:text-gray-400">
            Remembered it?{" "}
            <Link
              to="/"
              className="text-indigo-900 dark:text-indigo-400 hover:underline font-medium"
            >
              Back to log in
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - branding */}
      <div className="hidden md:flex w-1/2 bg-indigo-900 text-white flex-col items-center justify-center relative">
        <div className="flex flex-col items-center">
          <div className="w-24 h-24 flex items-center justify-center mb-6">
            <img src={logo} alt="Logo" />
          </div>
          <h2 className="text-7xl font-bold">Secure Chat AI</h2>
        </div>

        <div className="absolute top-4 md:top-20 right-4 md:right-20">
          <ThemeToggle theme={theme} setTheme={setTheme} />
        </div>
      </div>
    </div>
  );
}
